import { useState, useEffect, useRef, useCallback } from 'react'

export interface GeoPosition {
  lat: number
  lng: number
  accuracy: number
  heading: number | null
}

interface UseGeolocationResult {
  position: GeoPosition | null
  error: string | null
  isTracking: boolean
  /** Resolves the fresh fix, or null on error/unsupported. */
  recenter: () => Promise<GeoPosition | null>
}

/**
 * GPS position tracking hook using the browser Geolocation API.
 * Starts watching on mount, cleans up on unmount.
 * Returns current position, error state, and a recenter callback.
 */
export function useGeolocation(): UseGeolocationResult {
  const [position, setPosition] = useState<GeoPosition | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isTracking, setIsTracking] = useState(false)
  const watchIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser')
      return
    }

    const onSuccess = (pos: GeolocationPosition) => {
      setPosition({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        heading: pos.coords.heading,
      })
      setError(null)
      setIsTracking(true)
    }

    const onError = (err: GeolocationPositionError) => {
      switch (err.code) {
        case err.PERMISSION_DENIED:
          setError('Location permission denied')
          break
        case err.POSITION_UNAVAILABLE:
          setError('Location unavailable')
          break
        case err.TIMEOUT:
          setError('Location request timed out')
          break
        default:
          setError('Unknown location error')
      }
      setIsTracking(false)
    }

    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    })

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
    }
  }, [])

  /** Fresh fix on demand. Resolves the new position (also stored), or null if unavailable — the
   *  map's recentre flies to THIS, not the previous fix (map audit 2026-10-09). */
  const recenter = useCallback((): Promise<GeoPosition | null> => new Promise(resolve => {
    if (!navigator.geolocation) { resolve(null); return }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, heading: pos.coords.heading }
        setPosition(next)
        resolve(next)
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }), [])

  return { position, error, isTracking, recenter }
}
